import { Image, Modal, Space, Typography } from 'antd';
import { useState } from 'react';
import { extractMedia, hasMedia, extractText, type ExtractedMedia } from '../utils/media';

const { Text } = Typography;

// Chiều cao tối đa của ô kết quả ở chế độ gọn (danh sách) -> mọi dòng cao đều nhau.
const COMPACT_MAX_H = 72;

/** Nút "Xem video": bấm vào mới mở popup và phát (không mở tab mới, không tự tải). */
function VideoPlayButton({ url }: { url: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <a
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        style={{ cursor: 'pointer' }}
      >
        🎬 Xem video
      </a>
      <Modal
        open={open}
        title="Video"
        footer={null}
        width={760}
        destroyOnClose
        onCancel={() => setOpen(false)}
      >
        {open && (
          <video src={url} controls autoPlay style={{ width: '100%', borderRadius: 8 }} />
        )}
      </Modal>
    </>
  );
}

/**
 * Hiển thị kết quả (output) của job dưới dạng ảnh/video/audio + text.
 * - compact: dùng trong bảng (thumbnail nhỏ, CỐ ĐỊNH chiều cao, cắt bớt nếu dài).
 * - đầy đủ: dùng trong popup chi tiết.
 * - excludeUrls: các URL đầu vào cần loại bỏ (chỉ hiển thị output, không hiển thị input "echo").
 */
export function MediaResult({
  output,
  compact = false,
  excludeUrls,
}: {
  output: unknown;
  compact?: boolean;
  excludeUrls?: string[];
}) {
  const all = extractMedia(output);
  const exclude = new Set(excludeUrls ?? []);
  const media: ExtractedMedia = {
    images: all.images.filter((u) => !exclude.has(u)),
    videos: all.videos.filter((u) => !exclude.has(u)),
    audios: all.audios.filter((u) => !exclude.has(u)),
  };
  const text = extractText(output);

  if (!hasMedia(media) && !text) {
    return compact ? <Text type="secondary">—</Text> : <Text type="secondary">Không có kết quả hiển thị</Text>;
  }

  const thumb = compact ? 56 : 160;

  const body = (
    <Space direction="vertical" size={compact ? 4 : 12} style={{ width: '100%' }}>
      {media.images.length > 0 && (
        <Image.PreviewGroup>
          <Space wrap size={compact ? 4 : 8}>
            {(compact ? media.images.slice(0, 3) : media.images).map((url) => (
              <Image
                key={url}
                src={url}
                width={thumb}
                height={thumb}
                style={{ objectFit: 'cover', borderRadius: 8 }}
              />
            ))}
            {compact && media.images.length > 3 && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                +{media.images.length - 3}
              </Text>
            )}
          </Space>
        </Image.PreviewGroup>
      )}

      {media.videos.map((url) =>
        compact ? (
          <VideoPlayButton key={url} url={url} />
        ) : (
          <video key={url} src={url} controls style={{ maxWidth: '100%', borderRadius: 8 }} />
        ),
      )}

      {media.audios.map((url) =>
        compact ? (
          <a key={url} href={url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
            🔊 Nghe audio
          </a>
        ) : (
          <audio key={url} src={url} controls style={{ width: '100%' }} />
        ),
      )}

      {text &&
        (compact ? (
          <div
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              fontSize: 12,
              lineHeight: 1.4,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {text}
          </div>
        ) : (
          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{text}</div>
        ))}
    </Space>
  );

  // Ở danh sách: khóa cứng chiều cao để mọi dòng đều nhau, phần dư bị cắt.
  if (compact) {
    return <div style={{ maxHeight: COMPACT_MAX_H, overflow: 'hidden' }}>{body}</div>;
  }
  return body;
}
